import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import FeatherIcon from 'feather-icons-react';
import Button from 'components/base/Button';
import OrderDetailsTable from 'components/tables/OrderDetailsTable';
import { Alert, Card, Col, Dropdown, Form, Row, Spinner } from 'react-bootstrap';
import { Link, useParams } from 'react-router-dom';
import OrderSummaryDetails from 'components/common/OrderSummaryDetails';
import { currencyFormat } from 'helpers/utils';
import {
  BillingDetails,
  OtherDetails,
  ShippingDetails
} from 'components/modules/e-commerce/OrderDeliveryDetails';
import PageBreadcrumb from 'components/common/PageBreadcrumb';
import { defaultBreadcrumbItems } from 'data/commonData';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';
import {
  faChevronDown,
  faDownload,
  faPrint,
  faUndo
} from '@fortawesome/free-solid-svg-icons';
import { gql, useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { useEffect, useState } from 'react';
import { GENERATE_INVOICES } from 'api/graphql/queries/order/generateInvoiceLink';
import { SELLER_UPDATE_ORDER_STATUS } from 'api/graphql/mutations/updateOrderStatus';
import ToastManager, { showErrorToast } from 'components/orders/ToastManager';

// Define the order details query directly in this file to avoid import issues
const GET_SELLER_ORDER_DETAILS = gql`
  query SellerOrderDetails($orderId: Int!) {
    sellerOrderDetails(orderId: $orderId) {
      order_id
      order_number
      invoice_id
      order_status {
        code
        label
      }
      customer_firstname
      customer_lastname
      customer_email
      total_seller_amount
      shipping_method_product
      order_date
      items {
        item_id
        product_id
        name
        sku
        price
        total_price
        comission
        actual_seller_amount
        qty_commanded
        qty_invoiced
        qty_shipped
        qty_refunded
        TVA
        image
        is_variant
        seller_reference
        configurable_attributes {
          content
          label
        }
      }
      adress_informations {
        street
        city
        region
        postcode
        country
      }
      payment_method {
        code
        label
      }
      shipping_method {
        code
        label
      }
      shipping_type
    }
  }
`;

// Format status label for display by converting underscores to spaces and applying proper capitalization
const formatStatusLabel = (label: string): string => {
  // if (!label) return 'Unknown';
  
  // Replace underscores with spaces and capitalize each word
  return label
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

// Helper function to determine badge type and icon based on order status code
// This is the same mapping used in Orders.tsx for consistency
const getOrderStatusConfig = (code: string | undefined, t: any): { type: string; icon: string; label?: string } => {
  if (!code) return { type: 'secondary', icon: 'help-circle' };
  
  const statusCode = code.toLowerCase();
  const currentLanguage = i18n.language;
  
  // Function to get the appropriate label based on language
  const getLabel = (statusCode: string, englishLabel: string, translationKey: string): string => {
    // For English, show the original English label
    if (currentLanguage === 'en') {
      return englishLabel;
    }
    // For other languages (French, Arabic), use the translation system
    return t(translationKey);
  };
  
  // Map order status codes to badge configurations
  switch (statusCode) {
    case 'complete':
    case 'completed':
      return { type: 'success', icon: 'check-circle', label: getLabel(statusCode, 'Completed', 'orderStatus.completed') };
    
    case 'processing':
      return { type: 'warning', icon: 'clock', label: getLabel(statusCode, 'Processing', 'orderStatus.processing') };
    
    case 'pending':
      return { type: 'info', icon: 'clock', label: getLabel(statusCode, 'Pending', 'orderStatus.pending') };
    
    case 'closed':
      return { type: 'secondary', icon: 'lock', label: getLabel(statusCode, 'Closed', 'orderStatus.closed') };
    
    case 'canceled':
      return { type: 'danger', icon: 'x-circle', label: getLabel(statusCode, 'Canceled', 'orderStatus.canceled') };
    
    case 'returned':
      return { type: 'danger', icon: 'rotate-ccw', label: getLabel(statusCode, 'Returned', 'orderStatus.returned') };
    
    case 'received':
      return { type: 'success', icon: 'check', label: getLabel(statusCode, 'Received', 'orderStatus.received') };
    
    case 'transit':
    case 'en_cour_d_enlevement':
      return { type: 'info', icon: 'truck', label: getLabel(statusCode, 'In Transit', 'orderStatus.in_transit') };
    
    case 'en_cour_de_preparation':
      return { type: 'warning', icon: 'package', label: getLabel(statusCode, 'Being Prepared', 'orderStatus.being_prepared') };
    
    case 'prepared':
      return { type: 'success', icon: 'package', label: getLabel(statusCode, 'Prepared', 'orderStatus.prepared') };
    
    case 'receptionner_au_central':
      return { type: 'success', icon: 'home', label: getLabel(statusCode, 'Received at Central', 'orderStatus.receptionner_au_central') };
    
    case 'enlever':
      return { type: 'info', icon: 'arrow-up', label: getLabel(statusCode, 'Pick Up', 'orderStatus.enlever') };
    
    case 'transferer_au_central':
      return { type: 'info', icon: 'refresh-cw', label: getLabel(statusCode, 'Transferred to Central', 'orderStatus.transferer_au_central') };
    
    case 'en_consolidation':
      return { type: 'primary', icon: 'layers', label: getLabel(statusCode, 'In Consolidation', 'orderStatus.en_consolidation') };
      
    case 'ready_to_return':
      return { type: 'info', icon: 'corner-up-left', label: getLabel(statusCode, 'Ready to Return', 'orderStatus.ready_to_return') };
    
    default: {
      // For any other status
      const translationKey = `orderStatus.${code.toLowerCase()}`;
      
      if (currentLanguage === 'en') {
        // For English, format the code instead of using translation
        return { 
          type: 'secondary', 
          icon: 'help-circle',
          label: formatStatusLabel(code)
        };
      } else {
        // For other languages, try to use translation
        const translation = t(translationKey);
        
        // If translation key returns itself (no translation found), format the code
        return { 
          type: 'secondary', 
          icon: 'help-circle',
          label: translation !== translationKey ? translation : formatStatusLabel(code)
        };
      }
    }
  }
};

// Get Bootstrap badge variant for order status based on config type
const getOrderStatusBadgeVariant = (code: string | undefined, t: any): string => {
  return getOrderStatusConfig(code, t).type;
};

const OrderDetails = () => {
  const { t } = useTranslation();
  const { orderId } = useParams<{ orderId: string }>();
  
  // Convert orderId to number for GraphQL query
  const orderIdNumber = orderId ? parseInt(orderId, 10) : 0;
  
  // State for managing download state and order status update
  const [isDownloading, setIsDownloading] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  
  // GraphQL query for order details
  const { loading, error, data, refetch } = useQuery(GET_SELLER_ORDER_DETAILS, {
    variables: { orderId: orderIdNumber },
    skip: !orderIdNumber || isNaN(orderIdNumber),
    fetchPolicy: 'network-only'
  });
  
  const orderDetails = data?.sellerOrderDetails;
  
  // Show errors as toasts when there are errors
  useEffect(() => {
    if (error) {
      showErrorToast(`Error loading order details: ${error.message}`);
    }
    
    if (!loading && !error && !orderDetails) {
      showErrorToast("Order not found. Please check the order ID and try again.");
    }
  }, [error, loading, orderDetails]);
  
  // GraphQL mutation for updating order status
  const [updateOrderStatus, { loading: updateStatusLoading }] = useMutation(SELLER_UPDATE_ORDER_STATUS, {
    onCompleted: (data) => {
      setUpdatingStatus(false);
      // Refetch order details to get the updated status
      refetch();
    },
    onError: (error) => {
      setUpdatingStatus(false);
      showErrorToast('Error updating order status: ' + error.message);
      console.error('Error updating order status:', error.message);
    }
  });
  
  // Function to handle order status update
  const handleUpdateOrderStatus = (newStatus: string) => {
    if (!orderDetails?.order_id) {
      showErrorToast('Order ID is missing');
      return;
    }
    
    setUpdatingStatus(true);
    
    updateOrderStatus({
      variables: {
        orderIds: [orderDetails.order_id],
        orderStatus: newStatus
      }
    });
  };
  
  // Lazy query for generating invoice download link
  const [generateInvoiceQuery, { loading: downloadLoading }] = useLazyQuery(GENERATE_INVOICES, {
    fetchPolicy: 'no-cache',
    onCompleted: (data) => {
      handleDownloadResponse(data);
    },
    onError: (error) => {
      setIsDownloading(false);
      const errorMessage = 'Error generating invoice: ' + error.message;
      showErrorToast(errorMessage);
      console.error('Error generating invoice download link:', error.message);
    }
  });

  // Function to handle download response from GraphQL
  const handleDownloadResponse = (data: any) => {
    setIsDownloading(false);
    const response = data?.generateInvoices;
    
    if (!response || !response.success) {
      const errorMessage = 'Failed to generate invoice: ' + (response?.message || 'Unknown error');
      showErrorToast(errorMessage);
      console.error('Failed to generate invoice download link:', response?.message);
      return;
    }
    
    try {
      const downloadUrl = response.download_url;
      
      if (!downloadUrl) {
        const errorMessage = 'Download URL is missing in the response';
        showErrorToast(errorMessage);
        console.error('Download URL is missing in the response');
        return;
      }
      
      // Create download link and trigger download without page navigation
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', 'invoice.pdf'); // Force download attribute
      link.setAttribute('target', '_blank'); // Prevent navigation in current tab
      document.body.appendChild(link);
      link.click();
      setTimeout(() => {
        // Safely remove the link only if it's still a child of body
        if (link.parentNode === document.body) {
          document.body.removeChild(link);
        }
      }, 100);
    } catch (error) {
      const errorMessage = 'Error processing download: ' + (error instanceof Error ? error.message : String(error));
      showErrorToast(errorMessage);
      console.error('Error processing download:', error);
    }
  };
  
  // Function to trigger the invoice download
  const downloadInvoice = () => {
    const order_id = orderDetails?.order_id;
    
    if (!order_id) {
      console.error('Missing order information');
      return;
    }
    
    setIsDownloading(true);
    generateInvoiceQuery({
      variables: {
        order_ids: [order_id]
      }
    });
  };

  // Create breadcrumb items with order number if available
  const breadcrumbItems = orderDetails 
    ? [...defaultBreadcrumbItems, { label: `Order #${orderDetails.order_number}` }]
    : defaultBreadcrumbItems;

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '50vh' }}>
        <Spinner animation="border" role="status" variant="primary">
          <span className="visually-hidden">{t('loading')}</span>
        </Spinner>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '50vh' }}>
        <div className="text-center">
          <h5 className="mb-3">{t('orderDetails.unableToLoad')}</h5>
          <Button variant="primary" as={Link} to="/orders">
            {t('orderDetails.returnToOrdersList')}
          </Button>
        </div>
      </div>
    );
  }

  if (!orderDetails) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '50vh' }}>
        <div className="text-center">
          <h5 className="mb-3">{t('orderDetails.orderNotFound')}</h5>
          <Button variant="primary" as={Link} to="/orders">
            {t('orderDetails.returnToOrdersList')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Toast notification manager */}
      <ToastManager />
      <div className="mb-9">
        <h2 className="mb-1">
          {t('orderDetails.order')} <span>#{orderDetails.order_number}</span>
        </h2>
        <div className="d-flex flex-wrap flex-between-center mb-5 gap-3">
          <div className="d-flex flex-column gap-2">
            {/* Customer info with badge style */}
         
            
            {/* Order date with calendar icon */}
            <div className="d-flex align-items-center">
              <div className="bg-light rounded-pill py-1 px-3 d-flex align-items-center shadow-sm">
                <FeatherIcon 
                  icon="calendar" 
                  size={14} 
                  className="text-body-secondary me-2" 
                />
                <p className="text-body-tertiary mb-0 fs-10 fs-sm-9">
                  {t('orderDetails.orderDate')}: 
                  <span className="fw-semibold ms-2 text-body-secondary">
                    {new Date(orderDetails.order_date).toLocaleDateString(undefined, {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </span>
                </p>
              </div>
            </div>
          </div>
          
          <div className="d-flex gap-3 me-2 justify-content-center align-items-center">
            {!!orderDetails.invoice_id && orderDetails.invoice_id !== 0 && (
              <Button
                onClick={downloadInvoice}
                disabled={isDownloading}
                variant="warning"
                className="px-3 py-2"
                startIcon={
                  isDownloading ? (
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      style={{ width: '0.9rem', height: '0.9rem' }}
                    />
                  ) : (
                    <FontAwesomeIcon icon={faDownload} className="me-2" />
                  )
                }
              >
                {isDownloading ? t('orderDetails.downloading') : t('orderDetails.downloadInvoice')}
              </Button>
            )}
          </div>
        </div>
        
        
        <div className="mb-4">
          {(() => {
            // Get the status code from orderDetails
            const statusCode = orderDetails.order_status?.code?.toLowerCase() || 'processing';
            
            // Map status types to Bootstrap color classes
            const typeToColorMap = {
              'success': { 
                border: 'border-success', 
                bg: 'bg-success-subtle', 
                text: 'text-success',
                icon: faChevronDown
              },
              'warning': { 
                border: 'border-warning', 
                bg: 'bg-warning-subtle', 
                text: 'text-warning',
                icon: faChevronDown
              },
              'danger': { 
                border: 'border-danger', 
                bg: 'bg-danger-subtle', 
                text: 'text-danger',
                icon: faChevronDown
              },
              'info': { 
                border: 'border-info', 
                bg: 'bg-info-subtle', 
                text: 'text-info',
                icon: faChevronDown
              },
              'primary': { 
                border: 'border-primary', 
                bg: 'bg-primary-subtle', 
                text: 'text-primary',
                icon: faChevronDown
              },
              'secondary': { 
                border: 'border-secondary', 
                bg: 'bg-secondary-subtle', 
                text: 'text-secondary',
                icon: faChevronDown
              }
            };
            
            // Get the status configuration
            const statusConfig = getOrderStatusConfig(statusCode, t);
            
            // Get color scheme based on status type
            const colorVariant = typeToColorMap[statusConfig.type as keyof typeof typeToColorMap] || typeToColorMap['secondary'];
            
            return (
              <Card className={`border-top border-0 border-4 border-${getOrderStatusConfig(statusCode, t).type} shadow-sm`}>
                <Card.Body className="d-flex align-items-center p-3">
                  <div className={`p-3 ${colorVariant.bg} rounded-3 me-4`}>
                    <FeatherIcon 
                      icon={getOrderStatusConfig(statusCode, t).icon} 
                      size={20} 
                      className={colorVariant.text} 
                    />
                  </div>
                  <div>
                    <h5 className="mb-2 d-flex align-items-center">
                      {t('orderDetails.orderStatus')}: 
                      <span className={`${colorVariant.text} ms-2 d-flex align-items-center fw-bold`}>
                        {(() => {
                          // Direct mapping of status labels to translation keys
                          const statusLabel = orderDetails.order_status?.label;
                          
                          // Handle specific French status texts (case-sensitive)
                          if (statusLabel) {
                            // Direct matching for common French statuses
                            if (statusLabel === 'Terminée' || statusLabel === 'Terminee') {
                              return t('orderStatus.completed');
                            } 
                            if (statusLabel === 'En cours de traitement') {
                              return t('orderStatus.processing');
                            }
                            if (statusLabel === 'En préparation' || statusLabel === 'En cours de préparation') {
                              return t('orderStatus.being_prepared');
                            }
                            if (statusLabel === 'Préparé') {
                              return t('orderStatus.prepared');
                            }
                            if (statusLabel === 'En cours d\'enlèvement') {
                              return t('orderStatus.in_transit');
                            }
                            if (statusLabel === 'En attente') {
                              return t('orderStatus.pending');
                            }
                            if (statusLabel === 'Annulée') {
                              return t('orderStatus.canceled');
                            }
                            
                            // Pattern-based matching as fallback
                            if (statusLabel.includes('Terminé') || statusLabel.includes('Complet')) 
                              return t('orderStatus.completed');
                            if (statusLabel.includes('cours') && statusLabel.includes('enlève')) 
                              return t('orderStatus.in_transit');
                            if (statusLabel.includes('traitement') || statusLabel.includes('process')) 
                              return t('orderStatus.processing');
                            if (statusLabel.includes('prépar') || statusLabel.includes('prepar')) 
                              return t('orderStatus.being_prepared');
                            if (statusLabel.includes('annul') || statusLabel.includes('cancel')) 
                              return t('orderStatus.canceled');
                          }
                          
                          // Fallback to existing implementation
                          return getOrderStatusConfig(statusCode, t).label || 
                                 t(`orderStatus.${orderDetails.order_status?.code?.toLowerCase()}`) || 
                                 t('orderStatus.processing');
                        })()}
                      </span>
                    </h5>
                    <p className="mb-0 text-body-secondary d-flex align-items-center">
                      {t('orderDetails.paymentMethod')}: 
                      <span className="ms-2 fw-semibold">
                        {orderDetails.payment_method?.code 
                          ? orderDetails.payment_method.code.toLowerCase() === 'cashondelivery' 
                            ? t('paymentMethod.cashOnDelivery')
                            : t('paymentMethod.creditCard')
                          : t('paymentMethod.creditCard')}
                      </span>
                    </p>
                  </div>
                </Card.Body>
                
                {/* Dynamic Action Buttons based on order status */}
                <Card.Footer className="bg-white border-top p-3">
                  {(() => {
                    const statusCode = orderDetails.order_status?.code?.toLowerCase();
                    const shippingMethodProduct = parseInt(orderDetails.shipping_method_product || '0', 10);
                    const shippingType = orderDetails.shipping_type || 'other';
                    
                    // Don't show action buttons for FBW orders
                    if (shippingType === 'fbw') {
                      return (
                        <div className="border-start border-warning border-4 bg-warning-subtle p-3">
                          <div className="d-flex align-items-center">
                            <div className="text-warning me-3">
                              <i className="fas fa-shipping-fast fs-5"></i>
                            </div>
                            <div>
                              <p className="mb-1 fw-semibold text-warning fs-8">Fulfilled by Wamia Order</p>
                              <small className="text-muted">{t('fbwOrdersNotEditable') || 'FBW orders cannot be modified'}</small>
                            </div>
                          </div>
                        </div>
                      );
                    }
                    
                    // Status-specific action buttons for non-FBW orders
                    if (statusCode === 'processing') {
                      return (
                        <Button
                          onClick={() => handleUpdateOrderStatus('En_cour_de_preparation')}
                          disabled={updatingStatus}
                          variant="primary"
                          className="w-100"
                          startIcon={
                            updatingStatus ? (
                              <Spinner
                                as="span"
                                animation="border"
                                size="sm"
                                role="status"
                                aria-hidden="true"
                                style={{ width: '0.9rem', height: '0.9rem' }}
                              />
                            ) : (
                              <FeatherIcon icon="package" size={14} />
                            )
                          }
                        >
                          {updatingStatus ? t('orderDetails.updating') : t('orderDetails.prepare')}
                        </Button>
                      );
                    } else if (statusCode === 'en_cour_de_preparation') {
                      return (
                        <Button
                          onClick={() => handleUpdateOrderStatus('prepared')}
                          disabled={updatingStatus}
                          variant="success"
                          className="w-100"
                          startIcon={
                            updatingStatus ? (
                              <Spinner
                                as="span"
                                animation="border"
                                size="sm"
                                role="status"
                                aria-hidden="true"
                                style={{ width: '0.9rem', height: '0.9rem' }}
                              />
                            ) : (
                              <FeatherIcon icon="check-circle" size={14} />
                            )
                          }
                        >
                          {updatingStatus ? t('orderDetails.updating') : t('orderDetails.finishPreparing')}
                        </Button>
                      );
                    }
                    
                    // No action buttons for other statuses
                    return null;
                  })()}
                </Card.Footer>
              </Card>
            );
          })()}
        </div>
        
        <Row className="g-5 gy-7">
          <Col xs={12} xl={8} xxl={9}>
            <div className="mb-6">
              <h4 className="mb-3">{t('orderDetails.orderItems')}</h4>
              <OrderDetailsTable orderItems={orderDetails.items} />
            </div>
            <Row className="gx-4 gy-6 g-xl-7 justify-content-xl-start">
              <Col xs={12} md={4}>
                <BillingDetails 
                  customerDetails={{
                    name: `${orderDetails.customer_firstname} ${orderDetails.customer_lastname}`,
                    email: orderDetails.customer_email,
                    address: orderDetails.adress_informations
                  }} 
                />
              </Col>

              <Col xs={12} md={4}>
                <ShippingDetails 
                  shippingDetails={{
                    method: orderDetails.shipping_method?.label || 'Standard Shipping',
                    address: orderDetails.adress_informations
                  }}
                />
              </Col>

              <Col xs={12} md={4}>
                <OtherDetails 
                  paymentDetails={{
                    method: orderDetails.payment_method?.label || 'Credit Card',
                    status: orderDetails.order_status?.label || 'Processing'
                  }}
                />
              </Col>
            </Row>
          </Col>
          <Col xs={12} xl={4} xxl={3}>
            <Card className="shadow-sm border-0" style={{ position: "sticky", top: "20px" }}>
              <Card.Body className="p-2">
                <Card.Title as="h6" className="mb-1 fs-6">Order Summary</Card.Title>
                
                <hr className="my-1" />
                
                <div>
                  <OrderSummaryDetails orderDetails={orderDetails} />
                </div>
                
                <div className="d-flex justify-content-between align-items-center mt-1 pt-1 border-top">
                  <span className="fw-semibold mb-0" style={{ fontSize: '1.1rem' }}>{t('orderDetails.total')}:</span>
                  <span className="fw-bold mb-0" style={{ fontSize: '1.1rem' }}>
                    {currencyFormat(orderDetails.total_seller_amount, { minimumFractionDigits: 2 })}
                  </span>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default OrderDetails;
